import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

import { ActivityButton, SequenceNumberInput } from "../../../components/Common";
import { ParsedError } from "../../../interfaces";
import { CreateEducationSystemRequest } from "../../../interfaces/curriculum";
import { ErrorHandlingTypeEnum, SequenceEnum } from "../../../enums";
import { IMAGE_ACCEPT } from "../../../constants";
import { applyServerFormErrors } from "../../../services";
import {
  useAddEducationSystemMutation,
  useGetEducationSystemQuery,
  useUpdateEducationSystemMutation,
  useUploadEducationSystemIconMutation,
} from "../../../store/api/educationSystemApi";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: () => void;
};

const PLACEHOLDER_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64' viewBox='0 0 24 24' fill='%23ddd'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";

export const AddEditEducationSystemModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
}) => {
  const { t } = useTranslation();
  const [iconPreview, setIconPreview] = useState<string | null>(null);

  const { data: item, error: getError } = useGetEducationSystemQuery(
    id as string,
    { skip: !id || !show },
  );
  const [
    addItem,
    { isLoading: isAdding, isError: isErrorAdd, error: errorAdd },
  ] = useAddEducationSystemMutation();
  const [
    updateItem,
    { isLoading: isUpdating, isError: isErrorUpdate, error: errorUpdate },
  ] = useUpdateEducationSystemMutation();
  const [uploadIcon, { isLoading: isLoadingUploadIcon }] =
    useUploadEducationSystemIconMutation();

  const getEmptyValues = (): CreateEducationSystemRequest => ({
    name: { en: "", ar: "" },
    description: { en: "", ar: "" },
    code: "",
    sequenceNumber: 0,
    iconId: undefined,
  });

  const form = useFormik<CreateEducationSystemRequest>({
    enableReinitialize: false,
    initialValues: getEmptyValues(),
    validationSchema: Yup.object({
      name: Yup.object({
        en: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "englishValue" }),
          )
          .required(t("isNotEmpty", { displayName: "englishValue" })),
        ar: Yup.string()
          .max(
            100,
            t("maxLength", { maxLength: 100, displayName: "arabicValue" }),
          )
          .required(t("isNotEmpty", { displayName: "arabicValue" })),
      }),
      description: Yup.object({
        en: Yup.string().required(
          t("isNotEmpty", { displayName: "descriptionEn" }),
        ),
        ar: Yup.string().required(
          t("isNotEmpty", { displayName: "descriptionAr" }),
        ),
      }),
      code: Yup.string().required(t("isNotEmpty", { displayName: "code" })),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", { displayName: "sequenceNumber" }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        const payload: CreateEducationSystemRequest = {
          name: values.name,
          description: values.description,
          code: values.code,
          sequenceNumber: Number(values.sequenceNumber),
          iconId: values.iconId || undefined,
        };
        if (id) {
          await updateItem({ id, data: payload }).unwrap();
        } else {
          await addItem(payload).unwrap();
        }
        onClose();
      } catch {
        // Form field errors (e.g. duplicate code) are applied in the effect below.
      }
    },
  });

  const onClose = () => {
    form.resetForm({ values: getEmptyValues() });
    setIconPreview(null);
    onCloseClick();
  };

  const onChangeIcon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setIconPreview(reader.result as string);
    reader.readAsDataURL(file);
    try {
      const result = await uploadIcon({
        file,
        educationSystemId: id || undefined,
      }).unwrap();
      if (result?.id) {
        form.setFieldValue("iconId", result.id);
      }
    } catch {
      setIconPreview(item?.icon?.path || null);
    }
    e.target.value = "";
  };

  useEffect(() => {
    if (!show) {
      form.resetForm({ values: getEmptyValues() });
      setIconPreview(null);
      return;
    }

    if (id && item) {
      form.setValues({
        name: item.name,
        description: item.description || { en: "", ar: "" },
        code: item.code,
        sequenceNumber: item.sequenceNumber ?? 0,
        iconId: item.iconId,
      });
      setIconPreview(item.icon?.path || null);
      return;
    }

    if (!id) {
      form.resetForm({ values: getEmptyValues() });
      setIconPreview(null);
    }
  }, [show, id, item]);

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isAdding && isErrorAdd) _error = errorAdd as ParsedError;
    if (!isUpdating && isErrorUpdate) _error = errorUpdate as ParsedError;

    if (_error?.type === ErrorHandlingTypeEnum.FORM && _error.error) {
      applyServerFormErrors(form, _error.error);
    }
  }, [isAdding, isUpdating, isErrorAdd, isErrorUpdate]);

  return (
    <Modal id="addEditEducationSystem" isOpen={show} toggle={onClose} centered>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
          return false;
        }}
      >
        <ModalHeader className="p-3" toggle={onClose}>
          {id
            ? t("editItem", { item: "educationSystem" })
            : t("addItem", { item: "educationSystem" })}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label className="form-label">{t("icon")}</Label>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div className="profile-user position-relative d-inline-block">
                <img
                  src={iconPreview || PLACEHOLDER_ICON}
                  alt="icon"
                  className="rounded avatar-xl img-thumbnail"
                  style={{ objectFit: "cover", width: 80, height: 80 }}
                />
                <Label
                  htmlFor="education_system_icon"
                  className="profile-photo-edit avatar-xs rounded-circle bg-light text-body border"
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    cursor: "pointer",
                    padding: "0.25rem 0.5rem",
                  }}
                >
                  {isLoadingUploadIcon ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="ri-camera-fill" />
                  )}
                </Label>
                <Input
                  id="education_system_icon"
                  type="file"
                  className="d-none"
                  accept={IMAGE_ACCEPT}
                  onChange={onChangeIcon}
                  disabled={isLoadingUploadIcon || isAdding || isUpdating}
                />
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("code")}
              <span className="text-danger">*</span>
            </label>
            <Input
              id="code"
              name="code"
              value={form.values.code}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.code && form.errors.code)}
            />
            {form.touched.code && form.errors.code ? (
              <FormFeedback type="invalid">
                {form.errors.code as string}
              </FormFeedback>
            ) : null}
          </div>
          <SequenceNumberInput
            tabIndex={1}
            value={form.values.sequenceNumber}
            onChange={(e) => {
              e.preventDefault();
              const { value } = e.target;
              const regex =
                /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
              if (regex.test(value.toString())) {
                form.setFieldValue("sequenceNumber", value);
              } else {
                form.setFieldValue("sequenceNumber", "");
              }
            }}
            onBlur={form.handleBlur}
            invalid={
              !!(form.touched.sequenceNumber && form.errors.sequenceNumber)
            }
            sequenceType={SequenceEnum.EDUCATION_SYSTEM}
            onGenerateSequenceNumber={(v: number) => {
              form.setFieldValue("sequenceNumber", v);
            }}
            mode={id ? "edit" : "add"}
            request={{
              type: SequenceEnum.EDUCATION_SYSTEM,
            }}
          />
          {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
            <FormFeedback type="invalid" className="d-block">
              {form.errors.sequenceNumber as string}
            </FormFeedback>
          ) : null}
          <div className="mb-3">
            <label className="form-label">
              {t("nameEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              id="name.en"
              name="name.en"
              value={form.values.name.en}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.name?.en && form.errors.name?.en)}
            />
            {form.touched.name?.en && form.errors.name?.en ? (
              <FormFeedback type="invalid">
                {form.errors.name?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("nameAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              id="name.ar"
              name="name.ar"
              value={form.values.name.ar}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={!!(form.touched.name?.ar && form.errors.name?.ar)}
            />
            {form.touched.name?.ar && form.errors.name?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.name?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("descriptionEn")}
              <span className="text-danger">*</span>
            </label>
            <Input
              id="description.en"
              name="description.en"
              type="textarea"
              rows={3}
              value={form.values.description.en}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                !!(
                  form.touched.description?.en && form.errors.description?.en
                )
              }
            />
            {form.touched.description?.en && form.errors.description?.en ? (
              <FormFeedback type="invalid">
                {form.errors.description?.en as string}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <label className="form-label">
              {t("descriptionAr")}
              <span className="text-danger">*</span>
            </label>
            <Input
              style={{ fontFamily: "aljannat", direction: "rtl" }}
              id="description.ar"
              name="description.ar"
              type="textarea"
              rows={3}
              value={form.values.description.ar}
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              invalid={
                !!(
                  form.touched.description?.ar && form.errors.description?.ar
                )
              }
            />
            {form.touched.description?.ar && form.errors.description?.ar ? (
              <FormFeedback type="invalid">
                {form.errors.description?.ar as string}
              </FormFeedback>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Link to={"#"} onClick={onClose} className="btn btn-link link-success">
            <i className="ri-close-line me-1 align-middle"></i> {t("close")}
          </Link>
          <ActivityButton
            title="save"
            inProgressTitle="saving"
            color="success"
            type="submit"
            inProgress={isAdding || isUpdating}
            disabled={
              isAdding || isUpdating || isLoadingUploadIcon || !!getError
            }
          />
        </ModalFooter>
      </Form>
    </Modal>
  );
};
